if (!customElements.get('tooto-age-journal')) {
  class TootoAgeJournal extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.section = this.closest('.tooto-section');
      this.section?.classList.add('tooto-section--age-journal-active');

      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      this.destroy();
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.section && !this.section.querySelector('tooto-age-journal')) {
        this.section.classList.remove('tooto-section--age-journal-active');
      }

      this.isInitialized = false;
    }

    initialize() {
      this.stack = this.querySelector('[data-age-journal-stack]');
      this.items = this.stack
        ? Array.from(this.stack.children).filter((child) => child.matches('[data-age-journal-item]'))
        : [];

      this.imageListeners = [];
      this.trackedImages = new WeakSet();
      this.bindImageRefresh(this);
      this.bindLoadMoreButtons();
      this.bindResizeRefresh();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.queueRefresh();
        });
      }

      if (!window.gsap || !window.ScrollTrigger || this.items.length < 2) return;

      window.gsap.registerPlugin(window.ScrollTrigger);
      this.media = window.gsap.matchMedia();
      this.media.add(
        {
          desktop: '(min-width: 750px)',
          reduceMotion: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          const { desktop, reduceMotion } = context.conditions;
          const coverEnabled = this.dataset.scrollEnabled === 'true';

          if (!desktop || reduceMotion || !coverEnabled) return undefined;

          this.classList.add('is-scroll-enabled');

          this.items.forEach((item, index) => {
            item.style.zIndex = String(index + 1);

            if (index >= this.items.length - 1) return;

            const nextItem = this.items[index + 1];

            window.ScrollTrigger.create({
              trigger: item,
              start: 'bottom bottom',
              endTrigger: nextItem,
              end: 'top top',
              pin: item,
              pinSpacing: false,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            });
          });

          return () => {
            this.classList.remove('is-scroll-enabled');
            this.items.forEach((item) => {
              item.style.removeProperty('z-index');
            });
          };
        },
        this
      );

      this.queueRefresh();
    }

    bindImageRefresh(scope) {
      scope.querySelectorAll('img').forEach((image) => {
        if (image.complete || this.trackedImages.has(image)) return;

        const handleLoad = () => this.queueRefresh();
        this.trackedImages.add(image);
        image.addEventListener('load', handleLoad, { once: true });
        this.imageListeners.push({ image, handleLoad });
      });
    }

    bindLoadMoreButtons() {
      this.loadMoreButtons = Array.from(this.querySelectorAll('[data-age-journal-load-more]'));
      this.loadMoreButtons.forEach((button) => {
        button.addEventListener('click', this.handleLoadMoreClick);
      });
    }

    bindResizeRefresh() {
      if (!window.ResizeObserver) return;

      // Loaded cards and their media can change the pinned item's height after insertion.
      this.resizeObserver = new ResizeObserver(() => this.queueRefresh());
      this.querySelectorAll('[data-age-journal-articles]').forEach((articles) => {
        this.resizeObserver.observe(articles);
      });
    }

    handleLoadMoreClick(event) {
      const button = event.currentTarget;
      if (button.disabled) return;

      this.loadMoreArticles(button);
    }

    async loadMoreArticles(button) {
      const item = button.closest('[data-age-journal-item]');
      const layout = button.dataset.ageJournalLayout;
      const grid = item?.querySelector(`[data-age-journal-grid="${layout}"]`);
      const status = item?.querySelector('[data-age-journal-load-status]');
      const label = button.querySelector('[data-age-journal-load-more-label]');
      const sectionId = this.dataset.sectionId;
      const nextUrl = button.dataset.nextUrl || '';
      const loadCount = layout === 'mobile' ? 4 : 6;

      if (!item || !grid || !label || !sectionId || !layout) return;

      const originalLabel = label.textContent;
      const controller = new AbortController();
      this.loadMoreControllers ??= new Set();
      this.loadMoreControllers.add(controller);

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = button.dataset.loadingLabel || originalLabel;
      if (status) status.textContent = '';

      try {
        let followingUrl = nextUrl;
        let remainingCount = loadCount;
        const visitedUrls = new Set();

        remainingCount -= this.revealHiddenCards(grid, remainingCount);

        while (followingUrl && remainingCount > 0 && !visitedUrls.has(followingUrl)) {
          visitedUrls.add(followingUrl);
          const page = await this.fetchArticlePage(
            item.dataset.blockId,
            layout,
            followingUrl,
            sectionId,
            controller.signal
          );

          page.cards.forEach((card) => {
            card.hidden = true;
            grid.append(card);
            this.bindImageRefresh(card);
          });

          remainingCount -= this.revealHiddenCards(grid, remainingCount);
          followingUrl = page.nextUrl;
        }

        if (followingUrl) {
          button.dataset.nextUrl = followingUrl;
        } else {
          delete button.dataset.nextUrl;
        }

        if (!grid.querySelector(':scope > [hidden]') && !followingUrl) {
          button.closest('[data-age-journal-more]')?.remove();
        }

        this.queueRefresh();
      } catch (error) {
        if (error.name !== 'AbortError' && status) {
          status.textContent = button.dataset.errorMessage || '';
        }
      } finally {
        this.loadMoreControllers?.delete(controller);

        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          label.textContent = originalLabel;
        }
      }
    }

    revealHiddenCards(grid, limit) {
      const cards = Array.from(grid.querySelectorAll(':scope > [hidden]')).slice(0, limit);
      cards.forEach((card) => {
        card.hidden = false;
      });
      return cards.length;
    }

    async fetchArticlePage(blockId, layout, nextUrl, sectionId, signal) {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.hash = '';

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });

      if (!response.ok) throw new Error(`Unable to load articles: ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceItem = Array.from(documentFragment.querySelectorAll('[data-age-journal-item]')).find(
        (candidate) => candidate.dataset.blockId === blockId
      );
      const sourceGrid = sourceItem?.querySelector(`[data-age-journal-grid="${layout}"]`);
      const cards = sourceGrid ? Array.from(sourceGrid.children) : [];

      if (!sourceItem || !sourceGrid) throw new Error('The next article page is unavailable.');

      return {
        cards,
        nextUrl:
          sourceItem.querySelector(
            `[data-age-journal-load-more][data-age-journal-layout="${layout}"]`
          )?.dataset.nextUrl || '',
      };
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      this.queueRefresh();
    }

    queueRefresh() {
      if (!window.ScrollTrigger || this.refreshFrame) return;

      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        if (this.isConnected) window.ScrollTrigger.refresh();
      });
    }

    destroy() {
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = null;

      this.imageListeners?.forEach(({ image, handleLoad }) => {
        image.removeEventListener('load', handleLoad);
      });
      this.imageListeners = [];
      this.trackedImages = null;

      this.loadMoreButtons?.forEach((button) => {
        button.removeEventListener('click', this.handleLoadMoreClick);
      });
      this.loadMoreButtons = [];

      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      this.loadMoreControllers?.forEach((controller) => controller.abort());
      this.loadMoreControllers = null;

      this.media?.revert();
      this.media = null;
      this.classList.remove('is-scroll-enabled');
    }
  }

  customElements.define('tooto-age-journal', TootoAgeJournal);
}
